import React, { useState, useEffect } from 'react';
import { X, Calendar, CheckCircle, Loader2, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../contexts/DataContext';
import { saveInboxMessage } from '../services/storageService';
import { lockScroll, unlockScroll } from '../services/scrollLock';

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mkoezkng';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FormErrors = {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
};

export const ContactModal = () => {
  const { profile } = useData();
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const handleOpen = () => setIsOpen(true);
    window.addEventListener('open-contact-modal', handleOpen);
    return () => window.removeEventListener('open-contact-modal', handleOpen);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    lockScroll();
    return () => unlockScroll();
  }, [isOpen]);

  // Hide the floating chat launcher/widget while this modal is open (all
  // viewports). The ChatWidget listens for these same events, so we reuse that
  // mechanism instead of relying on brittle CSS selectors.
  useEffect(() => {
    if (!isOpen) return undefined;
    window.dispatchEvent(new Event('modal-opened'));
    return () => {
      window.dispatchEvent(new Event('modal-closed'));
    };
  }, [isOpen]);

  // Update a single field and clear its inline error as the user edits/selects.
  const updateField = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  // Custom validation. Native browser bubbles are disabled via `noValidate` on
  // the form, so this is the single source of truth for field errors.
  const validate = (): FormErrors => {
    const next: FormErrors = {};
    if (!formData.name.trim()) next.name = 'Please enter your name.';
    if (!formData.email.trim()) {
      next.email = 'Please enter your email.';
    } else if (!EMAIL_REGEX.test(formData.email.trim())) {
      next.email = 'Please enter a valid email address.';
    }
    if (!formData.subject) next.subject = 'Please select a subject.';
    if (!formData.message.trim()) next.message = 'Please enter a message.';
    return next;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    setStatus('sending');
    setErrorMessage(null);

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ ...formData, _subject: `New contact: ${formData.subject}` })
      });

      if (!response.ok) {
        throw new Error('Formspree submission failed');
      }

      try {
        await saveInboxMessage({
          id: crypto.randomUUID(),
          name: formData.name,
          email: formData.email,
          message: formData.message,
          date: new Date().toISOString(),
          read: false,
          type: 'contact',
          projectInterest: formData.subject
        });
      } catch (error) {
        console.error('Failed to save inbox message', error);
      }

      setStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error(error);
      setErrorMessage('Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  const closeModal = () => {
    setIsOpen(false);
    setStatus('idle');
    setErrorMessage(null);
    setErrors({});
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  // Shared field styling; the border flips to a red highlight when invalid.
  const fieldBase =
    'w-full bg-[#18181b] border rounded-xl px-3 py-2.5 text-white text-sm placeholder-zinc-600 focus:outline-none transition-colors disabled:opacity-70';
  const borderClass = (hasError: boolean) =>
    hasError ? 'border-red-500/50 focus:border-red-500/70' : 'border-white/10 focus:border-white/30';

  return (
    <AnimatePresence>
      {isOpen && (
        <div key="contact-modal" className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            onClick={closeModal}
          />

          {/* Modal Card - Адаптивный размер и скролл */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="relative w-full max-w-[440px] bg-[#09090b] border border-white/10 rounded-3xl shadow-2xl flex flex-col max-h-[85vh] overflow-hidden"
          >

            {/* Close Button - Фиксированный */}
            <button
              onClick={closeModal}
              className="absolute top-3 right-3 z-50 p-2 rounded-full bg-[#18181b] hover:bg-white/10 text-zinc-400 hover:text-white transition-colors border border-white/5"
            >
              <X size={16} />
            </button>

            {/* Scrollable Content — data-lenis-prevent keeps this inner scroll native
                even while Lenis is stopped by the modal lock. */}
            <div data-lenis-prevent className="overflow-y-auto p-5 md:p-6 custom-scrollbar">

              {/* Header */}
              <div className="mb-5 pr-8">
                <h2 className="text-xl font-semibold text-white">Get in touch</h2>
                <p className="text-zinc-400 text-xs mt-1">Open to strategic partnerships, investment conversations, media inquiries, and ideas worth discussing.</p>
              </div>

              <form action={FORMSPREE_ENDPOINT} method="POST" onSubmit={handleSubmit} noValidate className="space-y-3">

                {/* Name Input */}
                <div className="space-y-1">
                  <label className="text-[9px] font-bold text-zinc-500 uppercase tracking-wider pl-1">Full Name</label>
                  <input
                    name="name"
                    required
                    disabled={status === 'sending' || status === 'success'}
                    value={formData.name}
                    onChange={e => updateField('name', e.target.value)}
                    className={`${fieldBase} ${borderClass(!!errors.name)}`}
                    placeholder="Your name"
                  />
                  {errors.name && <p className="text-[11px] text-red-400 pl-1 mt-1">{errors.name}</p>}
                </div>

                {/* Email Input */}
                <div className="space-y-1">
                  <label className="text-[9px] font-bold text-zinc-500 uppercase tracking-wider pl-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    required
                    disabled={status === 'sending' || status === 'success'}
                    value={formData.email}
                    onChange={e => updateField('email', e.target.value)}
                    className={`${fieldBase} ${borderClass(!!errors.email)}`}
                    placeholder="you@company.com"
                  />
                  {errors.email && <p className="text-[11px] text-red-400 pl-1 mt-1">{errors.email}</p>}
                </div>

                {/* Subject Dropdown */}
                <div className="space-y-1">
                  <label className="text-[9px] font-bold text-zinc-500 uppercase tracking-wider pl-1">Subject</label>
                  <div className="relative">
                    <select
                      name="subject"
                      required
                      disabled={status === 'sending' || status === 'success'}
                      value={formData.subject}
                      onChange={e => updateField('subject', e.target.value)}
                      className={`${fieldBase} appearance-none cursor-pointer ${borderClass(!!errors.subject)}`}
                    >
                      <option value="" disabled className="text-zinc-500">Select a topic</option>
                      <option value="Partnership" className="bg-zinc-900">Partnership</option>
                      <option value="Investment" className="bg-zinc-900">Investment</option>
                      <option value="Media" className="bg-zinc-900">Media</option>
                      <option value="Collaboration" className="bg-zinc-900">Collaboration</option>
                      <option value="Other" className="bg-zinc-900">Other</option>
                    </select>
                    <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                  </div>
                  {errors.subject && <p className="text-[11px] text-red-400 pl-1 mt-1">{errors.subject}</p>}
                </div>

                {/* Message Input */}
                <div className="space-y-1">
                  <label className="text-[9px] font-bold text-zinc-500 uppercase tracking-wider pl-1">Message</label>
                  <textarea
                    name="message"
                    required
                    rows={3}
                    disabled={status === 'sending' || status === 'success'}
                    value={formData.message}
                    onChange={e => updateField('message', e.target.value)}
                    className={`${fieldBase} resize-none ${borderClass(!!errors.message)}`}
                    placeholder="Tell me about your vision..."
                  />
                  {errors.message && <p className="text-[11px] text-red-400 pl-1 mt-1">{errors.message}</p>}
                </div>

                {/* Send Button */}
                <button
                  type="submit"
                  disabled={status === 'sending' || status === 'success'}
                  className="w-full bg-white hover:bg-zinc-200 text-black font-medium rounded-full py-3 transition-all active:scale-[0.98] flex items-center justify-center gap-2 mt-2 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-white/5 text-sm"
                >
                  {status === 'sending' ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : status === 'success' ? (
                    <>Sent Successfully <CheckCircle size={18} /></>
                  ) : (
                    'Send Message'
                  )}
                </button>
                {status === 'success' && (
                  <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs text-zinc-200">
                    Thank you! Your message has been sent successfully.
                  </div>
                )}
                {status === 'error' && (
                  <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-xs text-red-200">
                    {errorMessage ?? 'Something went wrong. Please try again.'}
                  </div>
                )}
              </form>

              {/* Separator */}
              <div className="flex items-center gap-4 my-5">
                <div className="h-px flex-1 bg-white/5" />
                <span className="text-[9px] font-bold text-zinc-600 uppercase tracking-wider">OR</span>
                <div className="h-px flex-1 bg-white/5" />
              </div>

              {/* Booking Button */}
              {profile?.bookingUrl ? (
                <a
                  href={profile.bookingUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full bg-transparent border border-white/10 hover:bg-white/5 text-white font-medium rounded-full py-3 transition-all flex items-center justify-center gap-2 text-sm"
                >
                  <Calendar size={16} /> Book a call
                </a>
              ) : (
                <button disabled className="w-full border border-white/5 text-zinc-500 rounded-full py-3 cursor-not-allowed flex items-center justify-center gap-2 text-sm">
                   <Calendar size={16} /> Book a call (Not set)
                </button>
              )}

            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
